import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Link2,
  Search,
  RefreshCw,
  Plus,
  Pencil,
  Trash2,
  X,
  Copy,
  Check,
  ExternalLink,
  Rocket,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  getEntries,
  createEntry,
  updateEntry,
  deleteEntry,
  probeUrl,
} from '../../services/urlRegistryApi';

// ─── DATA ───────────────────────────────────────────────────────────────────

const URL_TYPES = [
  { key: 'backOffice', label: 'Back Office', color: '#6366f1' },
  { key: 'customDomain', label: 'Custom Domain', color: '#8b5cf6' },
  { key: 'storefront', label: 'Storefront', color: '#0ea5e9' },
  { key: 'simulcast', label: 'Simulcast', color: '#f59e0b' },
  { key: 'publicStorefront', label: 'Public Storefront', color: '#10b981' },
  { key: 'newStorefront', label: 'New Storefront', color: '#ec4899' },
  { key: 'kioskUrl', label: 'Kiosk URL', color: '#14b8a6' },
];

// ─── HELPERS ────────────────────────────────────────────────────────────────

/** Convert flat urls array from API into { urlType: url } map */
const urlsToMap = (urls) => {
  const map = {};
  (urls || []).forEach((u) => {
    map[u.urlType] = u.url;
  });
  return map;
};

/** Convert { urlType: url } map to API array format */
const mapToUrls = (map) =>
  Object.entries(map || {})
    .filter(([, v]) => v?.trim())
    .map(([urlType, url]) => ({ urlType, url: url.trim() }));

// ─── STATUS BADGE ───────────────────────────────────────────────────────────

const statusMap = {
  healthy: { label: 'OK', bg: 'bg-green-50 dark:bg-green-950', text: 'text-green-600', border: 'border-green-200 dark:border-green-800', dot: 'bg-green-500' },
  warning: { label: 'WARN', bg: 'bg-yellow-50 dark:bg-yellow-950', text: 'text-yellow-600', border: 'border-yellow-200 dark:border-yellow-800', dot: 'bg-yellow-500' },
  down: { label: 'DOWN', bg: 'bg-red-50 dark:bg-red-950', text: 'text-red-600', border: 'border-red-200 dark:border-red-800', dot: 'bg-red-500' },
  empty: { label: 'N/A', bg: 'bg-gray-50 dark:bg-slate-950', text: 'text-gray-400 dark:text-slate-500', border: 'border-gray-200 dark:border-slate-700', dot: 'bg-gray-300 dark:bg-slate-600' },
  checking: { label: '...', bg: 'bg-blue-50 dark:bg-blue-950', text: 'text-blue-500', border: 'border-blue-200 dark:border-blue-800', dot: 'bg-blue-400' },
};

const StatusBadge = ({ status }) => {
  const s = statusMap[status] || statusMap.empty;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-extrabold tracking-wider border ${s.bg} ${s.text} ${s.border}`}
      style={{ fontFamily: "'JetBrains Mono', monospace", minWidth: 42, justifyContent: 'center' }}
    >
      {(status === 'healthy' || status === 'down') && (
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`} />
      )}
      {s.label}
    </span>
  );
};

// ─── URL CELL ───────────────────────────────────────────────────────────────

const UrlCell = ({ url, status }) => {
  const [copied, setCopied] = useState(false);
  const [hovering, setHovering] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleOpen = (e) => {
    e.stopPropagation();
    const href = url.startsWith('http') ? url : `https://${url}`;
    window.open(href, '_blank');
  };

  if (!url) {
    return (
      <div className="flex items-center gap-2 px-2 py-1 text-gray-300 dark:text-slate-600 text-xs italic">
        <span>&mdash;</span>
        <StatusBadge status="empty" />
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 px-2 py-1 rounded-lg transition-colors relative"
      style={{ background: hovering ? '#f8fafc' : 'transparent' }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <StatusBadge status={status} />
      <a
        href={url.startsWith('http') ? url : `https://${url}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-gray-600 dark:text-gray-400 hover:text-indigo-600 truncate max-w-[320px] transition-colors"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
        title={url}
        onClick={(e) => e.stopPropagation()}
      >
        {url.replace(/^https?:\/\//, '')}
      </a>
      {hovering && (
        <div className="flex gap-1 shrink-0">
          <button
            onClick={handleCopy}
            title="Copy"
            className="w-6 h-6 rounded flex items-center justify-center text-xs transition-colors"
            style={{ background: copied ? '#dcfce7' : '#f3f4f6' }}
          >
            {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} className="text-gray-500" />}
          </button>
          <button
            onClick={handleOpen}
            title="Open"
            className="w-6 h-6 rounded flex items-center justify-center bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          >
            <ExternalLink size={12} className="text-gray-500 dark:text-slate-400" />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── ADD / EDIT MODAL ───────────────────────────────────────────────────────

const AddEditModal = ({ item, onSave, onClose, isNew }) => {
  const [name, setName] = useState(item?.name || '');
  const [entryType, setEntryType] = useState(item?.entryType || 'tenant');
  const [color, setColor] = useState(item?.color || '#6366f1');
  const [sortOrder, setSortOrder] = useState(item?.sortOrder ?? 0);
  const [urls, setUrls] = useState(() => (item ? urlsToMap(item.urls) : {}));

  const handleUrlChange = (key, val) => {
    setUrls((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      entryType,
      color,
      sortOrder,
      urls: mapToUrls(urls),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-xl max-h-[85vh] overflow-auto shadow-2xl animate-in"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 dark:border-slate-800">
          <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-100">
            {isNew ? 'Add New Entry' : `Edit ${item?.name}`}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-600 flex items-center justify-center transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-7 py-5 flex flex-col gap-4">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-3">
            <div>
              <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Permian"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-slate-950 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 outline-none transition"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                Type
              </label>
              <select
                value={entryType}
                onChange={(e) => setEntryType(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-slate-950 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 outline-none transition"
              >
                <option value="tenant">Tenant</option>
                <option value="environment">Environment</option>
                <option value="innovago">InnovaGO</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                Color
              </label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-11 h-10 p-1 rounded-lg border border-gray-200 dark:border-slate-700 cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
              Sort Order
            </label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-slate-950 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 outline-none transition"
            />
          </div>

          {/* URLs */}
          <div className="border-t border-gray-100 dark:border-slate-800 pt-4">
            <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
              URLs
            </label>
            {entryType === 'innovago' ? (
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-gray-500 dark:text-slate-400 min-w-[120px]">URL</span>
                <input
                  value={urls.url || ''}
                  onChange={(e) => handleUrlChange('url', e.target.value)}
                  placeholder="https://..."
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-xs bg-gray-50 dark:bg-slate-950 dark:text-gray-100 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 outline-none transition"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2.5">
                {URL_TYPES.map((ut) => (
                  <div key={ut.key} className="flex items-center gap-3">
                    <span
                      className="text-[11px] font-bold min-w-[120px]"
                      style={{ color: ut.color }}
                    >
                      {ut.label}
                    </span>
                    <input
                      value={urls[ut.key] || ''}
                      onChange={(e) => handleUrlChange(ut.key, e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-xs bg-gray-50 dark:bg-slate-950 dark:text-gray-100 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 outline-none transition"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-2">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2.5 rounded-lg border-none text-white text-sm font-bold transition-colors"
              style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
              }}
            >
              {isNew ? 'Add Entry' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────

export default function UrlRegistry() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [healthStatus, setHealthStatus] = useState({});
  const [isChecking, setIsChecking] = useState(false);
  const [modal, setModal] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const searchRef = useRef(null);

  // ── Load data ──────────────────────────────────────────────────────────

  const loadEntries = useCallback(async () => {
    try {
      const data = await getEntries();
      setEntries(data);
    } catch (err) {
      console.error('Failed to load entries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // ── Health check ───────────────────────────────────────────────────────

  const runHealthCheck = useCallback(async () => {
    if (isChecking) return;
    setIsChecking(true);
    const statuses = {};

    const allUrls = [];
    entries.forEach((entry) => {
      const urlMap = urlsToMap(entry.urls);
      const types = entry.entryType === 'innovago' ? ['url'] : URL_TYPES.map((t) => t.key);
      types.forEach((key) => {
        if (urlMap[key]) {
          allUrls.push({ entryId: entry.id, urlType: key, url: urlMap[key] });
        }
      });
    });

    // Probe in batches of 6 for performance
    const batchSize = 6;
    for (let i = 0; i < allUrls.length; i += batchSize) {
      const batch = allUrls.slice(i, i + batchSize);
      const results = await Promise.allSettled(
        batch.map(async ({ entryId, urlType, url }) => {
          try {
            const result = await probeUrl(url);
            return { entryId, urlType, status: result.reachable ? 'healthy' : (result.statusCode ? 'warning' : 'down') };
          } catch {
            return { entryId, urlType, status: 'down' };
          }
        })
      );
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          const { entryId, urlType, status } = r.value;
          if (!statuses[entryId]) statuses[entryId] = {};
          statuses[entryId][urlType] = status;
        }
      });
      // Update UI progressively
      setHealthStatus((prev) => ({ ...prev, ...statuses }));
    }

    setHealthStatus(statuses);
    setIsChecking(false);
  }, [entries, isChecking]);

  // ── Filtering & sorting ────────────────────────────────────────────────

  const environments = entries.filter((e) => e.entryType === 'environment');
  const tenants = entries.filter((e) => e.entryType === 'tenant');
  const innovaGoEntries = entries.filter((e) => e.entryType === 'innovago');

  const mainEntries = entries.filter((e) => e.entryType !== 'innovago');

  const filtered = mainEntries.filter((entry) => {
    const matchesTab =
      tab === 'all' ||
      (tab === 'environments' && entry.entryType === 'environment') ||
      (tab === 'tenants' && entry.entryType === 'tenant');
    const urlMap = urlsToMap(entry.urls);
    const matchesSearch =
      !search ||
      entry.name.toLowerCase().includes(search.toLowerCase()) ||
      Object.values(urlMap).some((u) => u?.toLowerCase().includes(search.toLowerCase()));
    return matchesTab && matchesSearch;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'type') return a.entryType.localeCompare(b.entryType);
    if (sortBy === 'urls') return (b.urls?.length || 0) - (a.urls?.length || 0);
    return 0;
  });

  // ── Stats ──────────────────────────────────────────────────────────────

  const totalUrls = entries.reduce((s, e) => s + (e.urls?.length || 0), 0);
  const healthyCount = Object.values(healthStatus).reduce(
    (s, entry) => s + Object.values(entry).filter((v) => v === 'healthy').length,
    0
  );
  const warningCount = Object.values(healthStatus).reduce(
    (s, entry) => s + Object.values(entry).filter((v) => v === 'warning').length,
    0
  );
  const downCount = Object.values(healthStatus).reduce(
    (s, entry) => s + Object.values(entry).filter((v) => v === 'down').length,
    0
  );

  // ── CRUD handlers ──────────────────────────────────────────────────────

  const handleSave = async (payload) => {
    try {
      if (modal.item?.id) {
        await updateEntry(modal.item.id, payload);
      } else {
        await createEntry(payload);
      }
      setModal(null);
      await loadEntries();
    } catch (err) {
      console.error('Save failed:', err);
      alert(`Save failed: ${err.message}`);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.name}"?`)) return;
    try {
      await deleteEntry(entry.id);
      await loadEntries();
    } catch (err) {
      console.error('Delete failed:', err);
      alert(`Delete failed: ${err.message}`);
    }
  };

  const getEntryHealth = (entryId) => {
    const s = healthStatus[entryId];
    if (!s) return 'checking';
    const vals = Object.values(s).filter((v) => v !== 'empty');
    if (vals.length === 0) return 'empty';
    if (vals.some((v) => v === 'down')) return 'down';
    if (vals.some((v) => v === 'warning')) return 'warning';
    return 'healthy';
  };

  // ── Stats cards config ─────────────────────────────────────────────────

  const stats = [
    { label: 'Total Entries', value: entries.length, sub: `${environments.length} envs · ${tenants.length} tenants`, accent: '#6366f1' },
    { label: 'Total URLs', value: totalUrls, sub: 'configured endpoints', accent: '#0ea5e9' },
    { label: 'Healthy', value: healthyCount, sub: 'responding OK', accent: '#22c55e' },
    { label: 'Warnings', value: warningCount, sub: 'needs attention', accent: '#f59e0b' },
    { label: 'Down', value: downCount, sub: 'unreachable', accent: '#ef4444' },
  ];

  // ── Loading state ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-gray-500 dark:text-slate-400">
        <div className="w-6 h-6 border-2 border-gray-300 dark:border-slate-600 border-t-indigo-600 rounded-full animate-spin" />
        <span className="ml-3 text-sm font-medium">Loading URL Registry...</span>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-[1440px] mx-auto px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-[10px] flex items-center justify-center shadow-md"
              style={{ background: 'linear-gradient(135deg, #6366f1, #a855f7)', boxShadow: '0 2px 8px rgba(99,102,241,0.25)' }}
            >
              <Link2 size={16} className="text-white" />
            </div>
            <div>
              <h1 className="text-[17px] font-extrabold text-gray-900 dark:text-gray-100 tracking-tight">
                URL Registry
              </h1>
              <p className="text-[11px] text-gray-400 dark:text-slate-500 font-medium -mt-0.5">
                InnovA Environment & Tenant URLs
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runHealthCheck}
              disabled={isChecking}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-60"
            >
              <RefreshCw size={14} className={isChecking ? 'animate-spin' : ''} />
              {isChecking ? 'Checking...' : 'Health Check'}
            </button>
            <button
              onClick={() => setModal({ item: null, isNew: true })}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border-none text-white text-xs font-bold transition-colors"
              style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
              }}
            >
              <Plus size={15} />
              Add Entry
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-5 gap-3 mb-6">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-gray-200 dark:border-slate-700"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                  {stat.label}
                </span>
              </div>
              <div
                className="text-[28px] font-black leading-none tracking-tight"
                style={{ color: stat.accent }}
              >
                {isChecking && !['Total Entries', 'Total URLs'].includes(stat.label) ? (
                  <span className="animate-pulse">&mdash;</span>
                ) : (
                  stat.value
                )}
              </div>
              <div className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">{stat.sub}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="flex items-center gap-3 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-[400px]">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500"
              />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search names, URLs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-8 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 outline-none transition"
              />
              {search && (
                <button
                  onClick={() => {
                    setSearch('');
                    searchRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded bg-gray-100 dark:bg-slate-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                >
                  <X size={11} />
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex bg-gray-100 dark:bg-slate-800 rounded-lg p-0.5">
              {[
                { key: 'all', label: `All (${mainEntries.length})` },
                { key: 'environments', label: `Environments (${environments.length})` },
                { key: 'tenants', label: `Tenants (${tenants.length})` },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
                    tab === t.key
                      ? 'bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-400 dark:text-slate-500 font-semibold">Sort:</span>
            {['name', 'type', 'urls'].map((s) => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold capitalize transition-colors ${
                  sortBy === s
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950 text-indigo-600'
                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-500 dark:text-slate-400 hover:border-gray-300 dark:hover:border-slate-600'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* InnovaGO Section */}
        {tab === 'all' && !search && innovaGoEntries.length > 0 && (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-4 mb-3.5">
            <div className="flex items-center gap-2 text-sm font-extrabold text-gray-900 dark:text-gray-100 mb-3">
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center text-xs"
                style={{ background: 'linear-gradient(135deg, #10b981, #14b8a6)' }}
              >
                <Rocket size={12} className="text-white" />
              </div>
              InnovaGO
            </div>
            <div className="flex gap-6">
              {innovaGoEntries.map((go) => {
                const goUrl = urlsToMap(go.urls).url;
                const goStatus = healthStatus[go.id]?.url;
                return (
                  <div key={go.id} className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 min-w-[140px]">
                      {go.name}
                    </span>
                    {goUrl ? (
                      <div className="flex items-center gap-2">
                        {goStatus && <StatusBadge status={goStatus} />}
                        <a
                          href={goUrl.startsWith('http') ? goUrl : `https://${goUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}
                        >
                          {goUrl}
                        </a>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300 dark:text-slate-600">&mdash;</span>
                    )}
                    <div className="flex gap-1">
                      <button
                        onClick={() => setModal({ item: go, isNew: false })}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <Pencil size={11} className="text-gray-400 dark:text-slate-500" />
                      </button>
                      <button
                        onClick={() => handleDelete(go)}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                      >
                        <Trash2 size={11} className="text-gray-400 dark:text-slate-500 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {/* Table Header */}
          <div
            className="grid items-center px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50/80 dark:bg-slate-900/80"
            style={{ gridTemplateColumns: '220px repeat(7, 1fr) 80px' }}
          >
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">
              Name
            </span>
            {URL_TYPES.map((ut) => (
              <span
                key={ut.key}
                className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500 flex items-center"
              >
                {ut.label}
              </span>
            ))}
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500 text-center">
              Actions
            </span>
          </div>

          {/* Rows */}
          {sorted.length === 0 ? (
            <div className="py-12 text-center text-gray-400 dark:text-slate-500">
              <Search size={40} className="mx-auto mb-2 text-gray-300 dark:text-slate-600" />
              <div className="text-sm font-semibold">No entries found</div>
              <div className="text-xs mt-1">Try a different search or filter</div>
            </div>
          ) : (
            sorted.map((entry, i) => {
              const isExpanded = expandedRow === entry.id;
              const overallHealth = getEntryHealth(entry.id);
              const urlMap = urlsToMap(entry.urls);
              const healthDotColor =
                overallHealth === 'healthy'
                  ? '#22c55e'
                  : overallHealth === 'warning'
                  ? '#f59e0b'
                  : overallHealth === 'down'
                  ? '#ef4444'
                  : overallHealth === 'checking'
                  ? '#3b82f6'
                  : '#d1d5db';

              return (
                <div
                  key={entry.id}
                  className={i < sorted.length - 1 ? 'border-b border-gray-100 dark:border-slate-800' : ''}
                >
                  {/* Row */}
                  <div
                    className="grid items-center px-4 min-h-[52px] cursor-pointer transition-colors hover:bg-gray-50/80 dark:hover:bg-slate-800/80"
                    style={{ gridTemplateColumns: '220px repeat(7, 1fr) 80px' }}
                    onClick={() => setExpandedRow(isExpanded ? null : entry.id)}
                  >
                    {/* Name cell */}
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-extrabold shrink-0"
                        style={{
                          background: `${entry.color}18`,
                          border: `1.5px solid ${entry.color}25`,
                          color: entry.color,
                        }}
                      >
                        {entry.name.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5 text-sm font-bold text-gray-900 dark:text-gray-100">
                          {entry.name}
                          <span
                            className="w-[7px] h-[7px] rounded-full shrink-0"
                            style={{ background: healthDotColor }}
                          />
                        </div>
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-px rounded ${
                            entry.entryType === 'environment'
                              ? 'bg-blue-50 dark:bg-blue-950 text-blue-600'
                              : 'bg-purple-50 dark:bg-purple-950 text-purple-600'
                          }`}
                        >
                          {entry.entryType === 'environment' ? 'ENV' : 'TENANT'}
                        </span>
                      </div>
                    </div>

                    {/* URL columns — compact view */}
                    {URL_TYPES.map((ut) => {
                      const url = urlMap[ut.key];
                      const status = healthStatus[entry.id]?.[ut.key] || 'checking';
                      return (
                        <div key={ut.key} className="overflow-hidden">
                          {url ? (
                            <div className="flex items-center gap-1 px-1">
                              <StatusBadge status={status} />
                              <span
                                className="text-[11px] text-gray-500 dark:text-slate-400 truncate max-w-[120px]"
                                style={{ fontFamily: "'JetBrains Mono', monospace" }}
                              >
                                {url
                                  .replace(/^https?:\/\//, '')
                                  .split('/')[0]
                                  .substring(0, 20)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-200 dark:text-slate-700 px-1">&mdash;</span>
                          )}
                        </div>
                      );
                    })}

                    {/* Actions */}
                    <div
                      className="flex justify-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        title="Edit"
                        onClick={() => setModal({ item: entry, isNew: false })}
                        className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <Pencil size={13} className="text-gray-400 dark:text-slate-500" />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => handleDelete(entry)}
                        className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                      >
                        <Trash2 size={13} className="text-gray-400 dark:text-slate-500 hover:text-red-500" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4">
                      <div className="bg-gray-50 dark:bg-slate-950 rounded-xl p-5 border border-gray-200 dark:border-slate-700">
                        <div className="flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-gray-300 mb-3">
                          <div
                            className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-extrabold"
                            style={{
                              background: `${entry.color}15`,
                              border: `1px solid ${entry.color}30`,
                              color: entry.color,
                            }}
                          >
                            {entry.name.charAt(0)}
                          </div>
                          {entry.name} — Full URL Details
                        </div>
                        <div className="flex flex-col gap-1">
                          {URL_TYPES.map((ut) => {
                            const url = urlMap[ut.key];
                            const status =
                              healthStatus[entry.id]?.[ut.key] || 'checking';
                            return (
                              <div
                                key={ut.key}
                                className="grid items-center gap-2 py-1.5 border-b border-gray-100 dark:border-slate-800 last:border-0"
                                style={{ gridTemplateColumns: '140px 1fr' }}
                              >
                                <span
                                  className="text-[11px] font-bold flex items-center gap-1.5"
                                  style={{ color: ut.color }}
                                >
                                  {ut.label}
                                </span>
                                <UrlCell url={url} status={status} />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="text-center py-6 text-[11px] text-gray-300 dark:text-slate-600 font-medium">
          InnovA URL Registry &middot; {entries.length} entries &middot; {totalUrls} URLs
          configured
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <AddEditModal
          item={modal.item}
          isNew={modal.isNew}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
